import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import moment, { Moment } from 'moment';
import { isEmpty, keys } from 'underscore';
import guid from '../../../../utils/Guid';
import { IPlotVariable } from '../../../../types/analytics';
import { IBestDataset } from '../../../../types/analytics';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { IScatterTableApiPayload } from '../../../../types/chart';
import { DT0Format } from '../../../../utils/StaticVariable';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import Api from '../../../../api/Api';
import { MarkerItem } from '../../../../types/home';
import useDatasetStore from '../../../../store/datasetStore';
import { usePointStore } from '../../../../store/pointStore';
import useAnalyticsStore from '../../../../store/analyticsStore';

interface UseScatterTableParams {
  coordinates: number[];
  previewDatasetId: string;
  categoryDataset: IBestDataset[];
  onDatasetChange: (item: string) => void;
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IScatterTableApiPayload) => void;
  error: string;
  data: any[];
  dateRange: [moment.Moment, moment.Moment];
  scatterTableLinks: string[];
  pointName: string;
  userInput: IAnalytcisUserInput;
  gridCellSize: string;
  waterDepth: string;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  updating: boolean;
}

export const useScatterTableParams = (params: UseScatterTableParams) => {
  const {
    coordinates,
    previewDatasetId,
    categoryDataset,
    onDatasetChange,
    enrichedMetadata,
    onUpdate,
    data,
    dateRange,
    pointName,
    userInput,
    gridCellSize,
    waterDepth,
    updateUserInput,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  } = params;

  const selfTriggered = useRef(false);

  const setEnrichedMetadata = useAnalyticsStore(
    (state) => state.setEnrichedMetadata
  );
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const fwePoints = usePointStore((state) => state.fwePoints);

  const [dataset, setDataset] = useState(userInput.ScatterTable.datasetX);
  const [datasetY, setDatasetY] = useState(userInput.ScatterTable.datasetY);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableX
  );
  const [variableY, setVariableY] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableY
  );
  const [tempVariableX, setTempVariableX] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableX
  );
  const [tempVariableY, setTempVariableY] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableY
  );
  const [month, setMonth] = useState(userInput.ScatterTable.month);
  const [intervalX, setIntervalX] = useState(userInput.ScatterTable.intervalX);
  const [intervalY, setIntervalY] = useState(userInput.ScatterTable.intervalY);
  const [tableData, setTableData] = useState<any[]>([]);
  const [column, setColumn] = useState<any[]>([]);
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [variablesY, setVariablesY] = useState<IPlotVariable[]>(
    userInput.ScatterTable.variablesY || []
  );
  const [isChangeDatasetY, setIsChangeDatasetY] = useState(false);
  const [startDate, setStartDate] = useState(
    userInput.ScatterTable.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.ScatterTable.endDate || moment(dateRange[1])
  );

  const getData = useCallback(
    (
      dataset1: string,
      dataset2: string,
      indexX: IPlotVariable,
      indexY: IPlotVariable,
      orderId: string,
      orderId2: string,
      requestId: string,
      requestId2: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX && indexY) {
        const payload: IScatterTableApiPayload = {
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          AcronymY: indexY.ShortName ? indexY.ShortName : indexY.Name,
          ColumnIndexX: indexX.Index,
          ColumnIndexY: indexY.Index,
          DatasetId: dataset1,
          DatasetId2: dataset2,
          EndDate: endDate,
          StartDate: startDate,
          IntervalCountX: intervalX,
          IntervalCountY: intervalY,
          Longitude: longitude.toString(),
          Latitude: latitude.toString(),
          Month: month,
          OrderId: orderId,
          OrderId2: orderId2,
          OriginalDatasetId: dataset1,
          OriginalOrderId: orderId,
          TitleX: indexX.Name,
          PlotFlag: '[3]',
          TitleY: indexY.Name,
          RequestId: requestId,
          RequestId2: requestId2,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, intervalX, intervalY, month]
  );

  const onUpdateData = useCallback(
    (dateRange?: any) => {
      if (
        dataset &&
        datasetY &&
        enrichedMetadata &&
        tempVariableX &&
        tempVariableY
      ) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === coordinates.join()
        );
        if (task) {
          let orderId2 = task.OrderId;
          let requestId2 = task.RequestId;
          if (dataset !== datasetY) {
            const task2 = enrichedMetadata.find(
              (task: IEnrichedMetadata) =>
                task.DatasetId === datasetY &&
                task.CoordinatesString === coordinates.join()
            );
            if (task2) {
              orderId2 = task2.OrderId;
              requestId2 = task2.RequestId;
            }
          }
          setVariableX(tempVariableX);
          setVariableY(tempVariableY);
          getData(
            dataset,
            datasetY,
            tempVariableX,
            tempVariableY,
            task.OrderId,
            orderId2,
            task.RequestId,
            requestId2,
            dateRange
              ? dateRange[0].format(DT0Format)
              : startDate.format(DT0Format),
            dateRange
              ? dateRange[1].format(DT0Format)
              : endDate.format(DT0Format)
          );
        }
      }
    },
    [
      enrichedMetadata,
      tempVariableX,
      tempVariableY,
      dataset,
      datasetY,
      startDate,
      endDate,
      coordinates,
      getData,
    ]
  );

  const doMonthChange = useCallback((selectedMonth: string) => {
    setMonth(selectedMonth);
  }, []);

  const doVariableChangeX = useCallback(
    (active: number) => {
      const item = variablesX.find((a) => a.Index === active)!;
      setTempVariableX(item);
    },
    [variablesX]
  );

  const doVariableChangeY = useCallback(
    (active: number) => {
      const item = variablesY.find((a) => a.Index === active)!;
      setTempVariableY(item);
    },
    [variablesY]
  );

  const doChangeIntervalX = useCallback(
    (active: number | string | null | undefined) => {
      if (active !== undefined && !isNaN(Number(active))) {
        setIntervalX(Number(active));
      }
    },
    []
  );

  const doChangeIntervalY = useCallback(
    (active: number | string | null | undefined) => {
      if (active !== undefined && !isNaN(Number(active))) {
        setIntervalY(Number(active));
      }
    },
    []
  );

  const doDatasetYChange = useCallback(
    async (active: string) => {
      let metadata = enrichedMetadata.find(
        (md: IEnrichedMetadata) =>
          md.DatasetId === active && md.CoordinatesString === coordinates.join()
      );

      if (!metadata) {
        try {
          setIsChangeDatasetY(true);
          metadata = await Api.metadata.getMetadata(active);
          const createFullExtractionTasks =
            await Api.service.createFullExtractionTasks(
              [metadata!],
              coordinates,
              pointName,
              gridCellSize,
              waterDepth,
              datasetBriefs,
              fwePoints
            );
          const addEnrichedMetadata = enrichedMetadata.concat(
            createFullExtractionTasks
          );
          setEnrichedMetadata(addEnrichedMetadata);
        } catch (err) {
          console.log(err);
        } finally {
          setIsChangeDatasetY(false);
        }
      }

      const withoutTime = metadata?.ItemsInfo.filter(
        (item: IPlotVariable) => item.Name !== 'Time'
      );
      if (!withoutTime) {
        return;
      }
      setVariablesY(withoutTime);
      setTempVariableY(withoutTime.length > 0 ? withoutTime[0] : null);
      setDatasetY(active);
    },
    [
      enrichedMetadata,
      coordinates,
      pointName,
      gridCellSize,
      waterDepth,
      datasetBriefs,
      fwePoints,
      setEnrichedMetadata,
    ]
  );

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const onselectDatasetX = useCallback(
    (value: string) => {
      onDatasetChange(value);
    },
    [onDatasetChange]
  );

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
    selfTriggered.current = true;
  }, [
    startDate,
    endDate,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  ]);

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      return isAfterEndDate
        ? isAfterEndDate
        : current.isBefore(dateRange[0], 'day');
    },
    [endDate, dateRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      return isBeforeStartDate
        ? isBeforeStartDate
        : current.isAfter(dateRange[1], 'day');
    },
    [startDate, dateRange]
  );

  const categories = useMemo(() => {
    const label = categoryDataset.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [categoryDataset]);

  useEffect(() => {
    updateUserInput({
      ScatterTable: {
        variablesY: variablesY,
        datasetX: dataset,
        datasetY: datasetY,
        variableX: variableX,
        variableY: variableY,
        startDate: startDate,
        endDate: endDate,
        month: month,
        intervalX: intervalX,
        intervalY: intervalY,
      },
    });
  }, [
    dataset,
    datasetY,
    variableX,
    variableY,
    startDate,
    endDate,
    month,
    intervalX,
    intervalY,
    variablesY,
    updateUserInput,
  ]);

  useEffect(() => {
    if (isForAllAnalytics && !isEmpty(data)) {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateData(globalDateRange);
        setStartDate(globalDateRange[0]);
        setEndDate(globalDateRange[1]);
      }
    }
  }, [isForAllAnalytics, globalDateRange]);

  useEffect(() => {
    if (
      previewDatasetId &&
      !isEmpty(categoryDataset) &&
      !isEmpty(coordinates)
    ) {
      setDataset(previewDatasetId);
      if (enrichedMetadata) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId &&
            task.CoordinatesString === coordinates.join()
        );
        if (task) {
          const defaultItems = task.ScatterTableDefaultItems.split(',');
          const itemsInfos = task.ItemsInfo.filter(
            (itemInfo: IPlotVariable) => itemInfo.Name !== 'Time'
          );
          const listVariableY = itemsInfos;
          if (itemsInfos.length > 1) {
            if (isEmpty(data)) {
              let indexX = itemsInfos[0];
              let indexY = itemsInfos[1];
              if (defaultItems.length > 1) {
                const defaultItem1 = task.ItemsInfo.find(
                  (itemInfo: IPlotVariable) =>
                    itemInfo.ShortName === defaultItems[0]
                );
                const defaultItem2 = task.ItemsInfo.find(
                  (itemInfo: IPlotVariable) =>
                    itemInfo.ShortName === defaultItems[1]
                );
                if (defaultItem1 !== undefined) {
                  indexX = defaultItem1;
                }
                if (defaultItem2 !== undefined) {
                  indexY = defaultItem2;
                }
              }
              setVariablesX(itemsInfos);
              setVariablesY(listVariableY);
              setDatasetY(previewDatasetId);
              setTempVariableX(indexX);
              setTempVariableY(indexY);
              setVariableX(indexX);
              setVariableY(indexY);
              let initialDateRange = dateRange;
              if (isForAllAnalytics) {
                initialDateRange = globalDateRange as [
                  moment.Moment,
                  moment.Moment
                ];
                setStartDate(initialDateRange[0]);
                setEndDate(initialDateRange[1]);
              } else {
                setStartDate(moment(initialDateRange[0]));
                setEndDate(moment(initialDateRange[1]));
              }
              selfTriggered.current = true;

              getData(
                previewDatasetId,
                previewDatasetId,
                indexX,
                indexY,
                task.OrderId,
                task.OrderId,
                task.RequestId,
                task.RequestId,
                dateRange[0].format(DT0Format),
                dateRange[1].format(DT0Format)
              );
            }
          }
        }
      }
    }
  }, [previewDatasetId, categoryDataset, enrichedMetadata, coordinates]);

  useEffect(() => {
    if (isEmpty(data)) {
      return;
    }
    const tempColumn: any[] = [];
    keys(data[0]).forEach((key) => {
      tempColumn.push({
        title: key,
        dataIndex: key,
        width: 80,
      });
    });
    const tempData = data.map((item) => {
      item.key = guid();
      return item;
    });

    setColumn(tempColumn);
    setTableData(tempData);
  }, [data]);

  return {
    dataset,
    datasetY,
    variableX,
    variableY,
    tempVariableX,
    tempVariableY,
    month,
    intervalX,
    intervalY,
    tableData,
    column,
    variablesX,
    variablesY,
    isChangeDatasetY,
    startDate,
    endDate,
    categories,
    onUpdateData,
    doMonthChange,
    doVariableChangeX,
    doVariableChangeY,
    doChangeIntervalX,
    doChangeIntervalY,
    doDatasetYChange,
    doStartDateChange,
    doEndDateChange,
    onselectDatasetX,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  };
};
